import { Box, BoxProps, Button, Flex, Link, Text } from '@chakra-ui/react';
import { useResource, useResources } from 'datx-jsonapi-react';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { useLecturePageQueryParams } from '@/components/ui/features/lecture/hooks';
import { queryThinLectureWithTag } from '@/fetchers/thinLectures';
import { queryTranscriptsByLectureId } from '@/queries/transcripts';
import { FoundIn, OmnisearchOccurrence } from '@/resources/OmnisearchOccurrence';

interface IOmnisearchDownloadableOccurrencesProps extends BoxProps {
  data: Array<OmnisearchOccurrence>;
}

export const OmnisearchDownloadableOccurrences: FC<IOmnisearchDownloadableOccurrencesProps> = ({ data, ...rest }) => {
  const { t } = useTranslation();
  const { id: lectureId } = useLecturePageQueryParams();
  const { data: transcripts } = useResources(queryTranscriptsByLectureId(lectureId));
  const { data: lecture } = useResource(queryThinLectureWithTag(lectureId));

  const videoOccurrences = data?.find((d) => d.foundIn === FoundIn.TRANSCRIPT_EN);
  const slideshow = data?.find((d) => d.foundIn === FoundIn.SLIDESHOW);
  const paper = data?.find((d) => d.foundIn === FoundIn.PAPER);

  if (!videoOccurrences && !slideshow && !paper) return null;

  const englishTranscript = transcripts.find((transcript) => transcript.language.locale === 'en');

  return (
    <Box p="4" pb="0" {...rest}>
      <Text fontSize="sm" fontWeight="medium" color="gray.700" textTransform="uppercase">
        {t('lectureOmnisearch.downloadableContentHeadline')}
      </Text>
      <Flex pt="4" flexDirection="column">
        {videoOccurrences && englishTranscript && (
          <Flex mb="4" alignItems="center">
            <Button
              textTransform="lowercase"
              as={Link}
              target="_blank"
              rel="noopener noreferrer"
              href={englishTranscript?.pdfUrl}
              mr="4"
              size="sm"
            >
              {t('common.download')}
            </Button>
            <Text fontWeight="medium" fontSize="sm" color="black">
              {t('newLecture.downloadTranscript')}
            </Text>
          </Flex>
        )}
        {slideshow && (
          <Flex mb="4" alignItems="center">
            <Button
              textTransform="lowercase"
              as={Link}
              target="_blank"
              rel="noopener noreferrer"
              href={lecture?.slideshowUrl}
              mr="4"
              size="sm"
            >
              {t('common.download')}
            </Button>
            <Text fontWeight="medium" fontSize="sm" color="black">
              {t('newLecture.downloadSlides')}
            </Text>
          </Flex>
        )}
        {paper && (
          <Flex mb="4" alignItems="center">
            <Button
              textTransform="lowercase"
              as={Link}
              target="_blank"
              rel="noopener noreferrer"
              href={lecture?.paperUrl}
              mr="4"
              size="sm"
            >
              {t('common.download')}
            </Button>
            <Text fontWeight="medium" fontSize="sm" color="black">
              {t('newLecture.downloadPaper')}
            </Text>
          </Flex>
        )}
      </Flex>
    </Box>
  );
};
